//  TODO: Create reusable toggle tip based on MUI tooltip
import { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { LelyButton } from '@/common/components';
import usePageMatch from '../App/hooks/usePageMatch';
import { RING_BUFFER_TOGGLE_TIP_LOCAL_STORAGE_KEY } from '@/containers/HubDevicesPage/constants';
import { LocalStorageGlobalObject } from '@/common/types';
import {
  TOGGLE_TIP_MAX_WIDTH,
  toggleTipBodySx,
  toggleTipCloseButtonSx,
  toggleTipHeaderSx,
} from '@/common/components/ToggleTip/toggleTip.styles';

const TOGGLE_TIP_DELAY_MS = 2000;
const TOGGLE_TIP_VIEWPORT_PADDING = 16;

type RingBufferOpenButtonProps = {
  onClick: () => void;
};

const RingBufferButton = ({ onClick }: RingBufferOpenButtonProps) => {
  const { ringBufferPageMatch } = usePageMatch();
  const ringBufferPageKey = ringBufferPageMatch?.split('/').pop();

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    setIsTooltipOpen(false);

    if (!ringBufferPageKey) {
      return;
    }

    const seenPages = localStorage.getObject<
      LocalStorageGlobalObject['ringBufferToggleTip'],
      typeof RING_BUFFER_TOGGLE_TIP_LOCAL_STORAGE_KEY
    >(RING_BUFFER_TOGGLE_TIP_LOCAL_STORAGE_KEY);

    if (seenPages?.[ringBufferPageKey]) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsTooltipOpen(true);
      localStorage.updateObjectProperty(
        RING_BUFFER_TOGGLE_TIP_LOCAL_STORAGE_KEY,
        ringBufferPageKey,
        true,
      );
    }, TOGGLE_TIP_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [ringBufferPageKey]);

  return (
    <Tooltip
      open={isTooltipOpen}
      placement="bottom"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'preventOverflow',
              options: { padding: TOGGLE_TIP_VIEWPORT_PADDING },
            },
          ],
        },
        tooltip: {
          sx: (theme) => ({
            '&&': {
              boxSizing: 'border-box',
              width: 'max-content',
              maxWidth: TOGGLE_TIP_MAX_WIDTH,
              padding: theme.lelySpacing.SM,
            },
            textAlign: 'left',
            color: theme.palette.text.light,
            '& .MuiTypography-root': { color: 'inherit' },
          }),
        },
      }}
      title={
        <Box data-testid="ring-buffer-toggle-tip">
          <Box display="flex" justifyContent="center">
            <Typography
              variant="md"
              sx={(theme) => ({
                ...toggleTipHeaderSx,
                fontWeight: 'bold',
                marginBottom: theme.lelySpacing.MD,
              })}
            >
              Retrieve buffered data
            </Typography>
            <LelyButton
              variant="secondary"
              aria-label="close toggle-tip"
              onClick={() => setIsTooltipOpen(false)}
              sx={toggleTipCloseButtonSx}
              data-testid="ring-buffer-toggle-tip-close-button"
              leadingIcon="lely-icon-close"
            />
          </Box>
          <Typography variant="body2" sx={toggleTipBodySx}>
            Data is locally buffered. Trigger a data upload to enable data
            visualization.
          </Typography>
        </Box>
      }
    >
      <LelyButton
        variant="secondary"
        leadingIcon="lely-icon-upload"
        sx={(theme) => ({
          whiteSpace: 'nowrap',
          padding: theme.lelySpacing.SM,
        })}
        onClick={() => {
          setIsTooltipOpen(false);
          onClick();
        }}
        data-testid="buffer-data-button"
        dense
      >
        Retrieve buffered data
      </LelyButton>
    </Tooltip>
  );
};

export default RingBufferButton;
